import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup } from "@mui/material";
import { defaultReportContasPagarAnaliticoForm, ReportContasPagarAnaliticoColumnsForm, ReportContasPagarAnaliticoForm, ReportContasPagarAnaliticoSchema } from "../../../schemas/reports/contasPagarAnaliticoReport";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { ContasPagarAnaliticoFilter } from "../../../../domain/models/contas_pagar_analitico_filter";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { TableOptions } from "../../../../../../common/components/table_options";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

/**
 * ReportMapaConcretagemModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  ddlCentroCusto: CentroCustoDropDown[];
  ddlBanco: DropDown[];
  ddlOrdemCompra: DropDown[];
  ddlSubconta: DropDown[];
  ddlFornecedor: DropDown[];
  ddlFuncionario: DropDown[];
  ddlProdutoServico: DropDown[];
  ddlNroMedicao: DropDown[];

  filtros: ContasPagarAnaliticoFilter | null,
  onClose: (openState: boolean) => void;
  onSubmit: (form: ReportContasPagarAnaliticoForm) => void;
  onSubmitColumns: (colunas: ReportContasPagarAnaliticoColumnsForm) => void;

};

export const ReportContasPagarAnaliticoModal = ({
  open,
  loading,
  saving,
  ddlCentroCusto,
  ddlBanco,
  ddlOrdemCompra,
  ddlSubconta,
  ddlFornecedor,
  ddlFuncionario,
  ddlProdutoServico,
  ddlNroMedicao,

  filtros,
  onClose,
  onSubmit,
  onSubmitColumns,
}: Props) => {

  const hookForm = useForm<ReportContasPagarAnaliticoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportContasPagarAnaliticoForm,
    resolver: zodResolver(ReportContasPagarAnaliticoSchema),
  });

  const [c] = hookForm.watch(['colunas']);

  const status = useMemo(() => {
    return [
      { id: 0, label: 'Em Aberto' },
      { id: 1, label: 'Pagas' },
      { id: 2, label: 'Outros' },
      { id: 3, label: 'Atrasadas' },
    ];
  }, []);


  const ordenacao = useMemo(() => {
    return [
      { id: 0, label: 'Data Pagar Crescente' },
      { id: 1, label: 'Data Pagar Decrescente' },
      { id: 2, label: 'Data Pago Crescente' },
      { id: 3, label: 'Data Pago Decrescente' },
    ];
  }, []);

  const selectAll = useMemo(() => {
    if (!c) return false;

    return (
      c.nroContasPagar &&
      c.centroCusto &&
      c.ordemCompra &&
      c.fornecedor &&
      c.bancoPagamento &&
      c.notaFiscal &&
      c.nroDocumento &&
      c.responsavel &&
      c.status &&
      c.titulo &&
      c.subconta &&
      c.dataPagar &&
      c.dataPago &&
      c.valorPagar &&
      c.valorPago &&
      c.funcionarioFolha &&
      c.contaBancaria &&
      c.observacao &&
      c.notaFiscalEspecial &&
      c.bdi &&
      c.nroMedicao &&
      c.totalBdi &&
      c.formaPagamento &&
      c.informacaoGeral &&
      c.bdi2 &&
      c.totalBdi2 &&
      c.dataCriacao &&
      c.dataCompetencia
    );
  }, [c]);

  useEffect(() => {
    if (open) {
      hookForm.reset({
        ...defaultReportContasPagarAnaliticoForm,
      });

      if (filtros && filtros.colunas && filtros.colunas.length > 0) {
        const colunasArr = filtros.colunas.split(',');

        hookForm.setValue('colunas', {
          nroContasPagar: colunasArr.includes('NR_CONTAS_PAGAR'),
          centroCusto: colunasArr.includes('CENTRO_CUSTO'),
          ordemCompra: colunasArr.includes('OC'),
          fornecedor: colunasArr.includes('FORNECEDOR'),
          bancoPagamento: colunasArr.includes('BANCO_PAGAMENTO'),
          notaFiscal: colunasArr.includes('NOTA_FISCAL'),
          nroDocumento: colunasArr.includes('NRO_DOCUMENTO'),
          responsavel: colunasArr.includes('RESPONSAVEL'),
          status: colunasArr.includes('STATUS'),
          titulo: colunasArr.includes('TITULO'),
          subconta: colunasArr.includes('SUBCONTA'),
          dataPagar: colunasArr.includes('DATA_A_PAGAR'),
          dataPago: colunasArr.includes('DATA_PAGO'),
          dataCompetencia: colunasArr.includes('DATA_COMPETENCIA'),
          valorPagar: colunasArr.includes('VALOR_A_PAGAR'),
          valorPago: colunasArr.includes('VALOR_PAGO'),
          funcionarioFolha: colunasArr.includes('FUNCIONARIO_FOLHA'),
          contaBancaria: colunasArr.includes('CONTA_BANCARIA'),
          observacao: colunasArr.includes('OBSERVACOES'),
          notaFiscalEspecial: colunasArr.includes('NOTA_FISCAL_ESPECIAL'),
          bdi: colunasArr.includes('BDI'),
          nroMedicao: colunasArr.includes('MEDICAO'),
          totalBdi: colunasArr.includes('TOTALBDI'),
          formaPagamento: colunasArr.includes('FORMA_PAGAMENTO'),
          informacaoGeral: colunasArr.includes('INFORMACOES_GERAIS'),
          bdi2: colunasArr.includes('BDI2'),
          totalBdi2: colunasArr.includes('TOTALBDI2'),
          dataCriacao: colunasArr.includes('DATA_CRIACAO')
        });
      }
    }
  }, [open, filtros, hookForm]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    hookForm.setValue('colunas', {
      nroContasPagar: checked,
      centroCusto: checked,
      ordemCompra: checked,
      fornecedor: checked,
      bancoPagamento: checked,
      notaFiscal: checked,
      nroDocumento: checked,
      responsavel: checked,
      status: checked,
      titulo: checked,
      subconta: checked,
      dataPagar: checked,
      dataPago: checked,
      valorPagar: checked,
      valorPago: checked,
      funcionarioFolha: checked,
      contaBancaria: checked,
      observacao: checked,
      notaFiscalEspecial: checked,
      bdi: checked,
      nroMedicao: checked,
      totalBdi: checked,
      formaPagamento: checked,
      informacaoGeral: checked,
      bdi2: checked,
      totalBdi2: checked,
      dataCriacao: checked,
      dataCompetencia: checked
    });
  }, [
    hookForm,
  ]);

  const handleToggleOptionChange = useCallback((field: keyof ReportContasPagarAnaliticoForm['colunas'], checked: boolean) => {
    const colunas = hookForm.getValues('colunas');
    colunas[field] = checked;
    hookForm.setValue('colunas', { ...colunas });
  }, [
    hookForm,
  ]);

  const handleLimparCampos = useCallback(() => {
    const colunas = hookForm.getValues('colunas');
    hookForm.reset({
      ...defaultReportContasPagarAnaliticoForm,
      colunas,
    });
  }, [hookForm]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title={'Relatório Contas Pagar Analítico'}
      width='80rem'
      confirmButtonLabel='Gerar relatório'
      confirmSavingButtonLabel='Gerando relatório...'
      savingLabel="Gerando"
      leftButtonSlot={
        <div className="w-full flex flex-row justify-between">
          <div>
            <PrimaryButton onClick={() => onSubmitColumns(c)}>Salvar Colunas</PrimaryButton>
          </div>
          <div className="w-[120px]">
            <PrimaryButton
              type="button"
              buttonColor="yellow"
              onClick={() => handleLimparCampos()}>
              Limpar campos
            </PrimaryButton>
          </div>
        </div>
      }
      onClose={() => onClose(false)}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full grid grid-rows-1 gap-2 p-4" >
        <div className="col-start-1 px-8 py-8">
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <div className="w-full flex gap-1">
                <DefaultFormInput
                  name='nro'
                  label='Nº'
                  reactHookForm={hookForm} />

                <DefaultFormInput
                  name='titulo'
                  label='Título/Pedido'
                  reactHookForm={hookForm} />

              </div>

              <DefaultFormMultiAutocomplete
                name='subconta'
                label='Subconta'
                options={ddlSubconta}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='ordemCompra'
                label='Ordem de Compra'
                options={ddlOrdemCompra}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='centroCusto'
                label='Centro de Custo'
                options={ddlCentroCusto}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='fornecedor'
                label='Fornecedor'
                options={ddlFornecedor}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='funcionarioFolha'
                label='Funcionário Folha'
                options={ddlFuncionario}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='medicao'
                label='Nro Medição'
                options={ddlNroMedicao}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='bancoPagamento'
                label='Banco de Pagamento'
                options={ddlBanco}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='notaFiscal'
                label='Nº da Nota Fiscal'
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='status'
                label='Status'
                options={status}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.label}
                reactHookForm={hookForm} />

              <div>
                Data a Pagar:
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataPagarDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataPagarAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />
                </div>
              </div>


              <div>
                Data Competência:
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataCompetenciaDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataCompetenciaAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />
                </div>
              </div>


              <div>
                Data Pago:
                <div className="w-full flex gap-1">

                  <FormDatePickerInput
                    name='dataPagoDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataPagoAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />

                </div>
              </div>

              <div>
                Data Criação:
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataCriacaoDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataCriacaoAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />

                </div>
              </div>


              <DefaultFormMultiAutocomplete
                name='responsavel'
                label='Responsável'
                options={ddlFuncionario}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <div className="mb-3">
                <DefaultFormMultiAutocomplete
                  name='produtosServicos'
                  label='Produtos/Serviços'
                  options={ddlProdutoServico}
                  getOptionKey={x => x.id}
                  getOptionLabel={x => x.title}
                  reactHookForm={hookForm} />
              </div>


              {/* <DefaultFormMultiAutocomplete
                name=''
                label='Responsável'
                options={ddlFuncionario}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} /> */}

            </>
          </FormContainer>
        </div>
        {/* col-start-7 col-span-5 */}
        <div className="col-start-6 col-span-5 px-8 py-8">
          <FormGroup>
            <TableOptions label="EXIBIR TODAS AS COLUNAS" checked={selectAll} onChange={c => handleToggleSelectAll(c)} />
            <div className="flex flex-col pl-4">
              <TableOptions checked={c?.nroContasPagar} label="Nº CONTA" onChange={v => handleToggleOptionChange('nroContasPagar', v)} />
              <TableOptions checked={c?.centroCusto} label="CENTRO DE CUSTO" onChange={v => handleToggleOptionChange('centroCusto', v)} />
              <TableOptions checked={c?.ordemCompra} label="ORDEM DE COMPRA" onChange={v => handleToggleOptionChange('ordemCompra', v)} />
              <TableOptions checked={c?.fornecedor} label="FORNECEDOR" onChange={v => handleToggleOptionChange('fornecedor', v)} />
              <TableOptions checked={c?.funcionarioFolha} label="FUNCIONÁRIO FOLHA" onChange={v => handleToggleOptionChange('funcionarioFolha', v)} />
              <TableOptions checked={c?.bancoPagamento} label="BANCO PAGAMENTO" onChange={v => handleToggleOptionChange('bancoPagamento', v)} />
              <TableOptions checked={c?.notaFiscal} label="NOTA FISCAL" onChange={v => handleToggleOptionChange('notaFiscal', v)} />
              <TableOptions checked={c?.nroDocumento} label="Nº DOCUMENTO" onChange={v => handleToggleOptionChange('nroDocumento', v)} />
              <TableOptions checked={c?.responsavel} label="RESPONSÁVEL" onChange={v => handleToggleOptionChange('responsavel', v)} />
              <TableOptions checked={c?.status} label="STATUS" onChange={v => handleToggleOptionChange('status', v)} />
              <TableOptions checked={c?.titulo} label="TÍTULO" onChange={v => handleToggleOptionChange('titulo', v)} />
              <TableOptions checked={c?.subconta} label="SUBCONTA" onChange={v => handleToggleOptionChange('subconta', v)} />
              <TableOptions checked={c?.dataPagar} label="DATA A PAGAR" onChange={v => handleToggleOptionChange('dataPagar', v)} />
              <TableOptions checked={c?.dataPago} label="DATA PAGO" onChange={v => handleToggleOptionChange('dataPago', v)} />
              <TableOptions checked={c?.dataCompetencia} label="DATA COMPETÊNCIA" onChange={v => handleToggleOptionChange('dataCompetencia', v)} />
              <TableOptions checked={c?.valorPagar} label="VALOR A PAGAR" onChange={v => handleToggleOptionChange('valorPagar', v)} />
              <TableOptions checked={c?.valorPago} label="VALOR PAGO" onChange={v => handleToggleOptionChange('valorPago', v)} />
              <TableOptions checked={c?.contaBancaria} label="CONTA BANCÁRIA" onChange={v => handleToggleOptionChange('contaBancaria', v)} />
              <TableOptions checked={c?.observacao} label="OBSERVAÇÃO" onChange={v => handleToggleOptionChange('observacao', v)} />
              <TableOptions checked={c?.notaFiscalEspecial} label="NOTA FISCAL ESPECIAL" onChange={v => handleToggleOptionChange('notaFiscalEspecial', v)} />
              <TableOptions checked={c?.bdi} label="BDI" onChange={v => handleToggleOptionChange('bdi', v)} />
              <TableOptions checked={c?.bdi2} label="BDI 2" onChange={v => handleToggleOptionChange('bdi2', v)} />
              <TableOptions checked={c?.totalBdi} label="TOTAL BDI" onChange={v => handleToggleOptionChange('totalBdi', v)} />
              <TableOptions checked={c?.totalBdi2} label="TOTAL BDI 2" onChange={v => handleToggleOptionChange('totalBdi2', v)} />
              <TableOptions checked={c?.nroMedicao} label="Nº MEDIÇÃO" onChange={v => handleToggleOptionChange('nroMedicao', v)} />
              <TableOptions checked={c?.formaPagamento} label="FORMA PAGAMENTO" onChange={v => handleToggleOptionChange('formaPagamento', v)} />
              <TableOptions checked={c?.informacaoGeral} label="INFORMAÇÃO GERAL" onChange={v => handleToggleOptionChange('informacaoGeral', v)} />
              <TableOptions checked={c?.dataCriacao} label="DATA CRIAÇÃO" onChange={v => handleToggleOptionChange('dataCriacao', v)} />
            </div>
          </FormGroup>
          <div className="mt-4">
            <DefaultFormAutocomplete
              name='ordenacao'
              label='Ordenação'
              options={ordenacao}
              getOptionKey={x => x.id}
              getOptionLabel={x => x.label}
              reactHookForm={hookForm}
            />
          </div>
        </div>
      </div>
    </BaseFormControlledModal>
  );
}